/**
 * YearRangeFilter Komponenti
 * 
 * Min-Max year seçimi için özel component
 * Year için range slider yerine dropdown kullanır
 */

'use client'

import { useState, useCallback, useEffect } from 'react'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'

export interface YearRangeFilterProps {
    fieldKey: string
    label: string
    min: number // Minimum yıl (örn: 1950)
    max: number // Maximum yıl (örn: 2025)
    value?: { min: number; max: number }
    onChange?: (value: { min: number; max: number }) => void
    className?: string
}

export default function YearRangeFilter({
    fieldKey,
    label,
    min,
    max,
    value,
    onChange,
    className = '',
}: YearRangeFilterProps) {
    const { getVariable } = useLanguageVariables()
    const [localMin, setLocalMin] = useState(value?.min ?? min)
    const [localMax, setLocalMax] = useState(value?.max ?? max)

    // Value prop değiştiğinde local state'i güncelle
    useEffect(() => {
        if (value) {
            setLocalMin(value.min)
            setLocalMax(value.max)
        }
    }, [value])

    // Yıl listesi oluştur (min'den max'a kadar)
    const yearOptions = Array.from({ length: max - min + 1 }, (_, i) => min + i).reverse()

    const handleMinChange = useCallback(
        (newMin: number) => {
            const clampedMin = Math.max(min, Math.min(newMin, localMax))
            setLocalMin(clampedMin)
            onChange?.({ min: clampedMin, max: localMax })
        },
        [min, localMax, onChange]
    )

    const handleMaxChange = useCallback(
        (newMax: number) => {
            const clampedMax = Math.min(max, Math.max(newMax, localMin))
            setLocalMax(clampedMax)
            onChange?.({ min: localMin, max: clampedMax })
        },
        [max, localMin, onChange]
    )

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-alsero-dark-gray">
                    {label}
                </label>
            )}
            
            {/* Min-Max Year Selectors */}
            <div className="flex items-center gap-3">
                {/* Min Year */}
                <div className="flex-1">
                    <label className="block text-xs text-alsero-light-gray mb-1.5">
                        {getVariable('filter_year_from', 'From')}
                    </label>
                    <select
                        value={localMin}
                        onChange={(e) => handleMinChange(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-alsero-dark-gray bg-white focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors cursor-pointer"
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Max Year */}
                <div className="flex-1">
                    <label className="block text-xs text-alsero-light-gray mb-1.5">
                        {getVariable('filter_year_to', 'To')}
                    </label>
                    <select
                        value={localMax}
                        onChange={(e) => handleMaxChange(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-alsero-dark-gray bg-white focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors cursor-pointer"
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    )
}

